import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Copy,
  Check,
  KeyRound,
  Download,
  ArrowRight,
  AlertTriangle,
} from "lucide-react";
import Button from "../components/common/Button";
import Panel from "../components/common/Panel";
import Navbar from "../layouts/Navbar";

export default function TwoFactorSetup({ embedded = false }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [verificationCode, setVerificationCode] = useState([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);
  const [copied, setCopied] = useState(false);
  const [copiedBackup, setCopiedBackup] = useState(false);

  // Hard-coded secret key for demo
  const secretKey = "JBSWY3DPEHPK3PXP";

  // Hard-coded backup codes for demo
  const backupCodes = [
    "ABCD-1234",
    "EFGH-5678",
    "IJKL-9012",
    "MNOP-3456",
    "QRST-7890",
    "UVWX-1234",
    "YZAB-5678",
    "CDEF-9012",
    "GHIJ-3456",
    "KLMN-7890",
  ];

  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace - move to previous input
    if (e.key === "Backspace" && !verificationCode[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newCode = [...verificationCode];
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }
    setVerificationCode(newCode);
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(secretKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    setCopiedBackup(true);
    setTimeout(() => setCopiedBackup(false), 2000);
  };

  const handleDownloadCodes = () => {
    const content = `Your 2FA Backup Codes\n${"=".repeat(30)}\n\n${backupCodes.join("\n")}\n\nImportant: Store these codes in a secure offline location.\nEach code can only be used once.`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "2fa-backup-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleVerify = () => {
    const code = verificationCode.join("");
    if (code.length === 6) {
      // For demo, accept any 6-digit code
      setStep(2);
    }
  };

  const isCodeComplete = verificationCode.every((digit) => digit !== "");

  const containerClass = embedded
    ? "w-full"
    : "app-bg min-h-screen flex flex-col";

  const panelContent = (
    <div className="text-white">
      {/* Step 1: QR Code Setup */}
      {step === 1 && (
        <>
          <h1 className="text-4xl font-bold text-white mb-2 text-center">
            Set Up Two-Factor Authentication
          </h1>
          <p className="text-green-500 font-medium mb-8 text-center">
            Scan the QR code with your authenticator app.
          </p>

          <div className="flex flex-col md:flex-row gap-8">
            {/* QR Code Section */}
            <div className="flex-1 flex flex-col items-center">
              <div className="bg-white p-4 rounded-lg mb-4">
                {/* Placeholder QR Code - in production, generate this dynamically */}
                <svg viewBox="0 0 256 256" className="w-48 h-48">
                  <rect width="256" height="256" fill="white" />
                  {/* Simplified QR pattern for demo */}
                  <g fill="black">
                    {/* Position patterns (corners) */}
                    <rect x="16" y="16" width="56" height="56" />
                    <rect x="24" y="24" width="40" height="40" fill="white" />
                    <rect x="32" y="32" width="24" height="24" />

                    <rect x="184" y="16" width="56" height="56" />
                    <rect x="192" y="24" width="40" height="40" fill="white" />
                    <rect x="200" y="32" width="24" height="24" />

                    <rect x="16" y="184" width="56" height="56" />
                    <rect x="24" y="192" width="40" height="40" fill="white" />
                    <rect x="32" y="200" width="24" height="24" />

                    {/* Data modules (simplified pattern) */}
                    <rect x="88" y="16" width="8" height="8" />
                    <rect x="104" y="16" width="8" height="8" />
                    <rect x="120" y="24" width="8" height="8" />
                    <rect x="88" y="32" width="8" height="8" />
                    <rect x="112" y="40" width="8" height="8" />
                    <rect x="136" y="32" width="8" height="8" />
                    <rect x="152" y="24" width="8" height="8" />
                    <rect x="168" y="40" width="8" height="8" />

                    <rect x="16" y="88" width="8" height="8" />
                    <rect x="32" y="96" width="8" height="8" />
                    <rect x="24" y="112" width="8" height="8" />
                    <rect x="48" y="104" width="8" height="8" />
                    <rect x="56" y="120" width="8" height="8" />

                    <rect x="88" y="88" width="8" height="8" />
                    <rect x="104" y="96" width="8" height="8" />
                    <rect x="120" y="88" width="8" height="8" />
                    <rect x="112" y="112" width="8" height="8" />
                    <rect x="136" y="104" width="8" height="8" />
                    <rect x="128" y="128" width="8" height="8" />
                    <rect x="144" y="120" width="8" height="8" />
                    <rect x="152" y="136" width="8" height="8" />

                    <rect x="184" y="88" width="8" height="8" />
                    <rect x="200" y="96" width="8" height="8" />
                    <rect x="216" y="104" width="8" height="8" />
                    <rect x="192" y="120" width="8" height="8" />
                    <rect x="224" y="128" width="8" height="8" />

                    <rect x="88" y="168" width="8" height="8" />
                    <rect x="104" y="184" width="8" height="8" />
                    <rect x="120" y="176" width="8" height="8" />
                    <rect x="136" y="192" width="8" height="8" />
                    <rect x="152" y="184" width="8" height="8" />
                    <rect x="168" y="200" width="8" height="8" />

                    <rect x="184" y="168" width="8" height="8" />
                    <rect x="200" y="184" width="8" height="8" />
                    <rect x="216" y="176" width="8" height="8" />
                    <rect x="192" y="200" width="8" height="8" />
                    <rect x="224" y="216" width="8" height="8" />
                  </g>
                </svg>
              </div>
              <p className="text-gray-200 text-sm text-center max-w-xs">
                Scan this QR code with an authenticator app like Google
                Authenticator or Authy.
              </p>
            </div>

            {/* Divider */}
            <div className="hidden md:block w-px bg-green-900/50" />

            {/* Manual Entry & Verification Section */}
            <div className="flex-1 space-y-8">
              {/* Manual Code Entry */}
              <div>
                <h3 className="text-white font-semibold mb-2">
                  Or Enter Code Manually
                </h3>
                <p className="text-gray-200 text-sm mb-4">
                  If you can't scan the QR code, enter this key into your app.
                </p>
                <div className="flex items-center gap-2 p-3 bg-[#0f2d1a] border border-green-800/50 rounded-lg">
                  <code className="flex-1 text-white font-mono text-sm">
                    {secretKey}
                  </code>
                  <button
                    onClick={handleCopyKey}
                    className="flex items-center gap-1 text-green-500 hover:text-green-400 transition-colors text-sm font-medium"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Verification Code Entry */}
              <div>
                <h3 className="text-white font-semibold mb-2">
                  Enter Verification Code
                </h3>
                <p className="text-gray-200 text-sm mb-4">
                  Enter the 6-digit code from your authenticator app.
                </p>
                <div className="flex gap-2 mb-6" onPaste={handlePaste}>
                  {verificationCode.map((digit, index) => (
                    <input
                      key={index}
                      id={`code-${index}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className="w-12 h-14 text-center text-xl font-mono text-white bg-[#0f2d1a] border border-green-800/50 rounded-lg focus:outline-none focus:border-green-500 transition-colors"
                    />
                  ))}
                </div>

                <Button onClick={handleVerify} disabled={!isCodeComplete}>
                  Verify & Enable 2FA
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Step 2: Backup Codes */}
      {step === 2 && (
        <>
          <h1 className="text-4xl font-bold text-white mb-2 text-center">
            Your 2FA Backup Codes
          </h1>
          <p className="text-green-500 font-medium mb-8 text-center">
            If you lose access to your authenticator app, you can use one of
            these codes to sign in.
          </p>

          {/* Backup Codes Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {backupCodes.map((code, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-4 bg-[#0f2d1a] border border-green-800/50 rounded-xl"
              >
                <KeyRound className="w-5 h-5 text-green-500 shrink-0" />
                <span className="text-white font-mono font-medium">
                  {code}
                </span>
              </div>
            ))}
          </div>

          {/* Warning Box */}
          <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/50 rounded-xl mb-6">
            <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
            <p className="text-yellow-500 text-sm">
              <span className="font-semibold">Important:</span> Store these
              codes in a secure offline location, like a password manager or a
              printed document. Each code can only be used once.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleCopyBackupCodes}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-[#0f2d1a] border border-green-800/50 rounded-lg text-white font-medium hover:border-green-500 transition-colors"
            >
              {copiedBackup ? (
                <>
                  <Check className="w-5 h-5" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-5 h-5" />
                  Copy Codes
                </>
              )}
            </button>
            <button
              onClick={handleDownloadCodes}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-[#0f2d1a] border border-green-800/50 rounded-lg text-white font-medium hover:border-green-500 transition-colors"
            >
              <Download className="w-5 h-5" />
              Download (.txt)
            </button>
            <div className="flex-1" />
            <Button
              onClick={() => setStep(3)}
              fullWidth={false}
            >
              I've Saved My Codes
              {/* <ArrowRight className="w-5 h-5" /> */}
            </Button>
          </div>
        </>
      )}

      {/* Step 3: Success Confirmation */}
      {step === 3 && (
        <>
          <h2 className="text-3xl font-bold text-white mb-4">
            2FA Enabled
            <br />
            Successfully!
          </h2>

          <p className="text-gray-200 mb-4">
            Your account is now protected with Two-Factor Authentication.
          </p>

          <p className="text-gray-200 mb-8">
            You will need your secondary device for future logins.
          </p>

          <div className="w-1/2 mx-auto">
            <Button onClick={() => navigate("/home")}>
              Continue to Dashboard (Login)
            </Button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className={containerClass}>
      {!embedded && <Navbar variant="minimal" />}

      {embedded ? (
        <div className="p-8 w-full">{panelContent}</div>
      ) : (
        <Panel
          centered
          maxWidth={step === 3 ? "xl" : "4xl"}
          className={step === 3 ? "text-center" : "p-8"}
        >
          {panelContent}
        </Panel>
      )}
    </div>
  );
}
